import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import {
  FiArrowLeft,
  FiCamera,
  FiCheck,
  FiCopy,
  FiExternalLink,
  FiImage,
  FiRefreshCw,
  FiUserPlus,
} from 'react-icons/fi';
import { IoScanOutline } from 'react-icons/io5';
import apiService from '../services/api';
import './Scan.css';

const SCANNER_ELEMENT_ID = 'qr-scanner-view';
const FILE_SCANNER_ELEMENT_ID = 'qr-file-scanner';

const OMNI_HOSTS = ['my-omni.link', 'www.my-omni.link', 'card.my-omni.link'];

const isUrlData = (data) =>
  /^(https?:\/\/|ftp:\/\/|mailto:|tel:|sms:|geo:|wifi:|market:|intent:|bitcoin:|ethereum:)/i.test(data) ||
  /^www\./i.test(data) ||
  /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}/.test(data) ||
  /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/i.test(data);

const normalizeUrl = (data) => {
  let target = data.trim();
  if (/^www\./i.test(target)) {
    return `https://${target}`;
  }
  if (!/^https?:\/\//i.test(target) && /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}/.test(target)) {
    return `https://${target}`;
  }
  return target;
};

const isOmniCardUrl = (data) =>
  data.includes('my-omni.link') || data.includes('/cards/card-') || data.includes('/card/');

const parseContact = (data) => {
  try {
    const json = JSON.parse(data);
    return {
      name: json.name || json.fullName || json.fn || '',
      title: json.title || json.jobTitle || '',
      company: json.company || json.businessName || '',
      email: json.email || json.mail || '',
      phone: json.phone || json.phoneNumber || json.tel || '',
      address: json.address || json.location || '',
      website: json.website || '',
      socialLinks: json.socialLinks || null,
      notes: json.notes || '',
    };
  } catch (_) {
    // Not JSON
  }

  if (/BEGIN:VCARD/i.test(data)) {
    const lines = data.split(/\r?\n/);
    const getValue = (key) => {
      const line = lines.find((entry) => entry.toUpperCase().startsWith(`${key.toUpperCase()}:`) || entry.toUpperCase().startsWith(`${key.toUpperCase()};`));
      if (!line) return '';
      const [, value = ''] = line.split(':');
      return value.trim();
    };
    return {
      name: getValue('FN'),
      title: getValue('TITLE'),
      company: getValue('ORG'),
      email: getValue('EMAIL'),
      phone: getValue('TEL'),
      address: getValue('ADR').replace(/;+$/g, '').replace(/;/g, ' '),
      website: getValue('URL'),
      socialLinks: null,
      notes: '',
    };
  }

  return null;
};

const Scan = () => {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const mountedRef = useRef(true);

  const [scannerState, setScannerState] = useState('starting'); // starting | scanning | result | error
  const [errorMessage, setErrorMessage] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    if (scanner) {
      try {
        const state = scanner.getState();
        if (state === 2 || state === 3) { // SCANNING or PAUSED
          await scanner.stop();
        }
        scanner.clear();
      } catch (_) {
        // Ignore cleanup errors
      }
      scannerRef.current = null;
    }
  }, []);

  const startScanner = useCallback(async () => {
    setErrorMessage('');
    setScannerState('starting');
    try {
      const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.DATA_MATRIX,
          Html5QrcodeSupportedFormats.AZTEC,
          Html5QrcodeSupportedFormats.PDF417,
        ],
        verbose: false,
      });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => {
          const data = decodedText.trim();
          stopScanner().then(() => {
            if (mountedRef.current) {
              setScanResult(data);
              setScannerState('result');
            }
          });
        },
        () => {}
      );
      if (scannerRef.current !== scanner) {
        // Cleaned up while the camera was starting (e.g. StrictMode remount)
        try {
          await scanner.stop();
          scanner.clear();
        } catch (_) {
          // Ignore cleanup errors
        }
        return;
      }
      if (mountedRef.current) {
        setScannerState('scanning');
      }
    } catch (err) {
      if (mountedRef.current) {
        setErrorMessage(
          'Camera access was denied or no camera was found. Allow camera permission, or upload an image with a QR code instead.'
        );
        setScannerState('error');
      }
    }
  }, [stopScanner]);

  useEffect(() => {
    mountedRef.current = true;
    const timer = setTimeout(() => startScanner(), 100);
    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
      stopScanner();
    };
  }, [startScanner, stopScanner]);

  const scannedIsUrl = useMemo(() => (scanResult ? isUrlData(scanResult) : false), [scanResult]);
  const scannedOmniCard = useMemo(
    () => (scanResult && scannedIsUrl ? isOmniCardUrl(scanResult) : false),
    [scanResult, scannedIsUrl]
  );
  const parsedContact = useMemo(
    () => (scanResult ? parseContact(scanResult) : null),
    [scanResult]
  );

  const handleOpenLink = () => {
    if (!scanResult) return;
    window.open(normalizeUrl(scanResult), '_blank', 'noopener,noreferrer');
  };

  const handleViewCard = () => {
    if (!scanResult) return;
    try {
      const url = new URL(normalizeUrl(scanResult));
      if (OMNI_HOSTS.includes(url.hostname) || url.hostname === window.location.hostname) {
        // Internal card link (e.g. /ramisj or /card/xyz) - navigate inside the app
        navigate(url.pathname);
        return;
      }
    } catch (_) {
      // Fall through to opening externally
    }
    window.open(normalizeUrl(scanResult), '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    if (!scanResult) return;
    try {
      await navigator.clipboard.writeText(scanResult);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // Clipboard unavailable
    }
  };

  const handleSaveContact = async () => {
    if (!parsedContact) return;
    setSaving(true);
    setSaveError('');
    try {
      const response = await apiService.createContact({
        fullName: parsedContact.name || 'Scanned Contact',
        email: parsedContact.email,
        phone: parsedContact.phone,
        company: parsedContact.company,
        jobTitle: parsedContact.title,
        website: parsedContact.website,
        address: parsedContact.address,
        notes: parsedContact.notes || `Scanned QR: ${scanResult}`,
        socialLinks: parsedContact.socialLinks,
        source: 'qr_scan',
      });
      if (response.ok) {
        setSaved(true);
      } else {
        setSaveError(response.error || 'Failed to save contact');
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleScanAgain = () => {
    setScanResult(null);
    setCopied(false);
    setSaved(false);
    setSaveError('');
    startScanner();
  };

  const handlePickImage = () => fileInputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    await stopScanner();
    try {
      const fileScanner = new Html5Qrcode(FILE_SCANNER_ELEMENT_ID, { verbose: false });
      const decoded = await fileScanner.scanFile(file, false);
      fileScanner.clear();
      if (mountedRef.current) {
        setScanResult(decoded.trim());
        setScannerState('result');
      }
    } catch (_) {
      if (mountedRef.current) {
        setErrorMessage('No QR code found in that image. Try a clearer photo.');
        setScannerState('error');
      }
    }
  };

  const showSaveButton = parsedContact && !scannedOmniCard;

  return (
    <div className="scan-container">
      <div className="scan-content">
        <div className="scan-topbar">
          <button className="scan-back-btn" onClick={() => navigate('/home')}>
            <FiArrowLeft size={20} />
          </button>
          <p className="scan-header-title">Scan QR Code</p>
          <div style={{ width: 40 }} />
        </div>

        {(scannerState === 'starting' || scannerState === 'scanning') && (
          <div className="scan-viewfinder-card">
            <div id={SCANNER_ELEMENT_ID} className="scan-viewfinder" />
            <div className="scan-status-row">
              <IoScanOutline size={18} />
              <span>{scannerState === 'starting' ? 'Starting camera...' : 'Point your camera at a QR code'}</span>
            </div>
          </div>
        )}

        {scannerState === 'error' && (
          <div className="scan-viewfinder-card">
            <div className="scan-error-state">
              <FiCamera size={40} />
              <p>{errorMessage}</p>
              <button className="scan-btn primary" onClick={startScanner}>
                <FiRefreshCw size={16} /> Retry Camera
              </button>
            </div>
          </div>
        )}

        {scannerState === 'result' && (
          <div className="scan-result-card">
            <h3 className="scan-result-title">Scan Result</h3>

            {parsedContact ? (
              <div className="scan-contact-preview">
                {parsedContact.name && <div className="scan-contact-name">{parsedContact.name}</div>}
                {parsedContact.title && <div className="scan-contact-line">{parsedContact.title}{parsedContact.company ? ` @ ${parsedContact.company}` : ''}</div>}
                {parsedContact.email && <div className="scan-contact-line">{parsedContact.email}</div>}
                {parsedContact.phone && <div className="scan-contact-line">{parsedContact.phone}</div>}
                {parsedContact.website && <div className="scan-contact-line">{parsedContact.website}</div>}
              </div>
            ) : (
              <div className="scan-raw-data">{scanResult}</div>
            )}

            <div className="scan-result-actions">
              {scannedOmniCard && (
                <button className="scan-btn primary" onClick={handleViewCard}>
                  <FiExternalLink size={16} /> View Card
                </button>
              )}
              {scannedIsUrl && !scannedOmniCard && (
                <button className="scan-btn primary" onClick={handleOpenLink}>
                  <FiExternalLink size={16} /> Open Link
                </button>
              )}
              {showSaveButton && !saved && (
                <button className="scan-btn primary" onClick={handleSaveContact} disabled={saving}>
                  <FiUserPlus size={16} /> {saving ? 'Saving...' : 'Save Contact'}
                </button>
              )}
              {saved && (
                <div className="scan-saved-badge">
                  <FiCheck size={16} /> Saved to Contacts
                </div>
              )}
              {saveError && <div className="scan-save-error">{saveError}</div>}
              <button className="scan-btn secondary" onClick={handleCopy}>
                <FiCopy size={16} /> {copied ? 'Copied' : 'Copy'}
              </button>
              <button className="scan-btn secondary" onClick={handleScanAgain}>
                <FiRefreshCw size={16} /> Scan Again
              </button>
            </div>
          </div>
        )}

        <div id={FILE_SCANNER_ELEMENT_ID} style={{ display: 'none' }} />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button className="scan-upload-btn" onClick={handlePickImage}>
          <FiImage size={18} />
          <span>Scan from image</span>
        </button>
      </div>
    </div>
  );
};

export default Scan;
